/**
 * This code was AUTOGENERATED using the codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type IAccountMeta,
  type IInstruction,
  type IInstructionWithAccounts,
  type IInstructionWithData,
  type ReadonlyAccount,
} from '@solana/kit';
import { LIGHTHOUSE_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  LogLevel,
  getAccountInfoAssertionDecoder,
  getAccountInfoAssertionEncoder,
  getLogLevelDecoder,
  getLogLevelEncoder,
  type AccountInfoAssertion,
  type AccountInfoAssertionArgs,
  type LogLevelArgs,
} from '../types';

export const ASSERT_ACCOUNT_INFO_DISCRIMINATOR = 5;

export function getAssertAccountInfoDiscriminatorBytes() {
  return getU8Encoder().encode(ASSERT_ACCOUNT_INFO_DISCRIMINATOR);
}

export type AssertAccountInfoInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountTargetAccount extends string | IAccountMeta<string> = string,
  TRemainingAccounts extends readonly IAccountMeta<string>[] = [],
> = IInstruction<TProgram> &
  IInstructionWithData<Uint8Array> &
  IInstructionWithAccounts<
    [
      TAccountTargetAccount extends string
        ? ReadonlyAccount<TAccountTargetAccount>
        : TAccountTargetAccount,
      ...TRemainingAccounts,
    ]
  >;

export type AssertAccountInfoInstructionData = {
  discriminator: number;
  logLevel: LogLevel;
  assertion: AccountInfoAssertion;
};

export type AssertAccountInfoInstructionDataArgs = {
  logLevel?: LogLevelArgs;
  assertion: AccountInfoAssertionArgs;
};

export function getAssertAccountInfoInstructionDataEncoder(): Encoder<AssertAccountInfoInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['logLevel', getLogLevelEncoder()],
      ['assertion', getAccountInfoAssertionEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: ASSERT_ACCOUNT_INFO_DISCRIMINATOR,
      logLevel: value.logLevel ?? LogLevel.Silent,
    })
  );
}

export function getAssertAccountInfoInstructionDataDecoder(): Decoder<AssertAccountInfoInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['logLevel', getLogLevelDecoder()],
    ['assertion', getAccountInfoAssertionDecoder()],
  ]);
}

export function getAssertAccountInfoInstructionDataCodec(): Codec<
  AssertAccountInfoInstructionDataArgs,
  AssertAccountInfoInstructionData
> {
  return combineCodec(
    getAssertAccountInfoInstructionDataEncoder(),
    getAssertAccountInfoInstructionDataDecoder()
  );
}

export type AssertAccountInfoInput<
  TAccountTargetAccount extends string = string,
> = {
  /** Target account to be asserted */
  targetAccount: Address<TAccountTargetAccount>;
  logLevel?: AssertAccountInfoInstructionDataArgs['logLevel'];
  assertion: AssertAccountInfoInstructionDataArgs['assertion'];
};

export function getAssertAccountInfoInstruction<
  TAccountTargetAccount extends string,
  TProgramAddress extends Address = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
>(
  input: AssertAccountInfoInput<TAccountTargetAccount>,
  config?: { programAddress?: TProgramAddress }
): AssertAccountInfoInstruction<TProgramAddress, TAccountTargetAccount> {
  // Program address.
  const programAddress = config?.programAddress ?? LIGHTHOUSE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    targetAccount: { value: input.targetAccount ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  const instruction = {
    accounts: [getAccountMeta(accounts.targetAccount)],
    programAddress,
    data: getAssertAccountInfoInstructionDataEncoder().encode(
      args as AssertAccountInfoInstructionDataArgs
    ),
  } as AssertAccountInfoInstruction<TProgramAddress, TAccountTargetAccount>;

  return instruction;
}

export type ParsedAssertAccountInfoInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly IAccountMeta[] = readonly IAccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Target account to be asserted */
    targetAccount: TAccountMetas[0];
  };
  data: AssertAccountInfoInstructionData;
};

export function parseAssertAccountInfoInstruction<
  TProgram extends string,
  TAccountMetas extends readonly IAccountMeta[],
>(
  instruction: IInstruction<TProgram> &
    IInstructionWithAccounts<TAccountMetas> &
    IInstructionWithData<Uint8Array>
): ParsedAssertAccountInfoInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = instruction.accounts![accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      targetAccount: getNextAccount(),
    },
    data: getAssertAccountInfoInstructionDataDecoder().decode(instruction.data),
  };
}
